/**
 * Display helpers for Paper Pricing (ledger row 132).
 *
 * Prices, counts and sentences arrive from the server already decided; nothing
 * here works a price out. Values stay the server's strings end to end and are
 * only regrouped for reading ("1419.7500" shows as "1,419.75").
 */
import { canonicalizeAmount } from '../../utils/amountFormat';
import { extractApiError } from '../../utils/errorMessages';

// Price points ("bates") as backend/market_pricing/models.py names them.
export const BATE_LABELS = { c: 'Close', h: 'High', l: 'Low', u: 'Index' };

// The three a person may pick for a typed price or a benchmark's default (AC2, AC10).
export const PRICE_POINTS = [
    { value: 'c', label: 'Close' },
    { value: 'h', label: 'High' },
    { value: 'l', label: 'Low' },
];

export const PRICE_STATUS_LABELS = { confirmed: 'Confirmed', manual: 'Manual' };

// Choice lists mirrored from backend/market_pricing/models.py, only to fill the
// pickers. The server validates every save, so a list that drifts from the model
// shows up as a refusal in the server's words, never as a quietly wrong value.
export const PROVIDERS = [
    { value: 'platts', label: 'Platts' },
    { value: 'argus', label: 'Argus' },
    { value: 'ice', label: 'ICE' },
    { value: 'other', label: 'Other' },
];

export const REGIONS = [
    { value: 'asia', label: 'Asia' },
    { value: 'middle_east', label: 'Middle East' },
    { value: 'europe', label: 'Europe' },
    { value: 'americas', label: 'Americas' },
    { value: 'africa', label: 'Africa' },
    { value: 'other', label: 'Other' },
];

export const INSTRUMENT_TYPES = [
    { value: 'swap', label: 'Swap' },
    { value: 'future', label: 'Future' },
];

export const CONTRACT_PERIODS = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' },
];

export const SETTLEMENT_TYPES = [
    { value: 'cash', label: 'Cash settled' },
    { value: 'physical', label: 'Physically settled' },
];

// Written out rather than asked of the browser: en-GB now says "Sept" in some
// browsers and "Sep" in others, and the desk's own sheets say "Sep".
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const groupThousands = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * A price as the desk reads it: grouped, at least two decimals, and every
 * decimal the server actually holds -- never rounded away.
 * "770.0400" -> "770.04", "1419.75" -> "1,419.75", "156.4670" -> "156.467",
 * "700" -> "700.00". '' when there is no number.
 */
export const formatPrice = (value) => {
    const canon = canonicalizeAmount(value);
    if (canon === '') return '';
    const negative = canon.startsWith('-');
    const [intPart, fracRaw = ''] = (negative ? canon.slice(1) : canon).split('.');
    const frac = fracRaw.replace(/0+$/, '').padEnd(2, '0');
    return `${negative ? '-' : ''}${groupThousands(intPart)}.${frac}`;
};

/** 12223 -> "12,223". */
export const formatCount = (value) => groupThousands(String(value));

/** "2026-09-01" -> "1 Sep 2026". Read as a calendar day, never through a time zone. */
export const formatDay = (iso) => {
    if (!iso) return '';
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
    if (!match) return String(iso);
    const [, year, month, day] = match;
    return `${Number(day)} ${MONTHS[Number(month) - 1]} ${year}`;
};

/** "2026-09-01" -> "Sep 2026". */
export const formatMonth = (iso) => {
    const match = /^(\d{4})-(\d{2})/.exec(String(iso || ''));
    if (!match) return String(iso || '');
    return `${MONTHS[Number(match[2]) - 1]} ${match[1]}`;
};

/** An instant -> "1 Sep 2026, 11:04", in the reader's own time. */
export const formatDateTime = (iso) => {
    if (!iso) return '';
    const when = new Date(iso);
    if (Number.isNaN(when.getTime())) return String(iso);
    const hours = String(when.getHours()).padStart(2, '0');
    const minutes = String(when.getMinutes()).padStart(2, '0');
    return `${when.getDate()} ${MONTHS[when.getMonth()]} ${when.getFullYear()}, ${hours}:${minutes}`;
};

/** Today as YYYY-MM-DD in the reader's own calendar. */
export const todayIso = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
};

/** "USD" + "MT" -> "USD/MT". */
export const unitLabel = (currency, unit) => [currency, unit].filter(Boolean).join('/');

/** plural(1, 'index', 'indexes') -> "1 index"; plural(20, ...) -> "20 indexes". */
export const plural = (count, one, many) => `${formatCount(count)} ${count === 1 ? one : many}`;

/**
 * The list a server answer must carry. A missing list is refused in words: read
 * as "nothing to show", a broken answer would look exactly like an empty desk.
 */
export const requireList = (data, key) => {
    const list = data ? data[key] : undefined;
    if (!Array.isArray(list)) {
        throw new Error('The server sent an answer this screen cannot read. Refresh the page and try again.');
    }
    return list;
};

/**
 * A form refusal from the server ({"code": ["..."], "unit": ["..."]}) as
 * { field: sentence }. Null when the answer is one sentence ({"error": ...})
 * instead, which the caller shows as it is.
 */
export const fieldErrorsFrom = (error) => {
    const data = error?.response?.data;
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    if (data.error || data.detail || data.message) return null;
    const fields = {};
    Object.entries(data).forEach(([field, value]) => {
        const parts = Array.isArray(value) ? value : [value];
        const text = parts.filter((part) => typeof part === 'string').join(' ');
        if (text) fields[field] = text;
    });
    return Object.keys(fields).length ? fields : null;
};

/**
 * A refusal of a button (Deactivate, Activate) as the server worded it. When
 * the server answers with a field ({"benchmark_id": ["..."]}) there is no box
 * to put it under, so the sentences are shown without the field's code name.
 */
export const refusalText = (error, fallback) => {
    const fields = fieldErrorsFrom(error);
    return fields ? Object.values(fields).join(' ') : extractApiError(error, fallback);
};

/**
 * The part of a form refusal that has no box on screen to sit under, as one
 * sentence -- so nothing the server said is dropped.
 */
export const unplacedFieldErrors = (fieldErrors, shownFields) => Object.entries(fieldErrors)
    .filter(([field]) => !shownFields.includes(field))
    .map(([, text]) => text)
    .join(' ');
